<script setup lang="ts">
import { computed, ref } from "vue";

import { $can } from "@/services/UtilitiesService";
import { usePointStore } from "@/stores/point-store";

import CheckVerified from "./_CheckVerified.vue";
import StringTooltip from "@/components/Tooltip/StringTooltip.vue";

import type { CheckPointData } from "@/models/Point";

withDefaults(defineProps<{
  isScore?: boolean;
  checkpointReference: string;
  checkpoint: Omit<CheckPointData, "reference">;
}>(), {  })
defineSlots<{ scc125(): void }>();

const checkFileReference = computed(() => (__props.checkpoint.check.scope === "record" ? __props.checkpoint.appliesTo : undefined));

const resolvedAIEvaluation = computed(() =>
  __props.checkpoint.evaluation === "unsure" || __props.checkpoint.evaluation === "unset" ? "" : __props.checkpoint.evaluation,
);
const resolvedEvaluation = computed(() => __props.checkpoint.userEvaluation ?? resolvedAIEvaluation.value);

const evaluation = ref(resolvedEvaluation.value);
const evaluationVerified = ref(__props.checkpoint.userEvaluation != null);

const canEditEvaluation = computed(() => $can("amendCheckpointEvaluations"));

const evaluationOptions = computed(() => {
  const options: Array<{ label: string; value: NonNullable<CheckPointData["userEvaluation"] | ""> }> = [
    { label: "Yes", value: "positive" },
    { label: "No", value: "negative" },
    // { label: "Neutral", value: "neutral" },
  ];

  if (resolvedAIEvaluation.value === "") options.push({ label: "Unclear", value: "" });

  return options;
});

function updateEvaluation(userEvaluation: CheckPointData["userEvaluation"]) {
  if (!canEditEvaluation.value || __props.checkpoint == null) return;

  try {
    // Optimistically update
    evaluationVerified.value = userEvaluation != null;
    evaluation.value = userEvaluation ?? resolvedAIEvaluation.value;

    usePointStore().updateCheckPoint(__props.checkpointReference, checkFileReference.value, {
      settingEvaluation: true,
      userEvaluation,
    });
  } catch (error) {
    // In the case of an error, revert to the original state
    evaluationVerified.value = __props.checkpoint.userEvaluation != null;
    evaluation.value = resolvedEvaluation.value;
  }
}
</script>

<template>
  <td aria-hidden="true" :class="{ '!tw-border-t-0': isScore }">
    <div class="tw-grid tw-place-items-center">
      <i class="fa-regular fa-circle-check tw-w-4 tw-text-center" />
    </div>
  </td>
  <StringTooltip :item="checkpoint.check.longDescription" class="tw-max-w-sm tw-select-none">
    <td class="!tw-pl-0">
      <div class="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-4">
        <span>{{ checkpoint.check.shortDescription }}</span>
        <div aria-hidden="true" class="tw-grow" />
        <select
          :value="evaluation"
          :disabled="!canEditEvaluation"
          class="tw-w-20 tw-shrink-0 tw-rounded tw-border tw-border-black/40 tw-p-1"
          @click.stop
          @change="
            (e) =>
              updateEvaluation(((e.target as HTMLSelectElement)?.value || null) as CheckPointData['userEvaluation'])
          "
        >
          <option v-for="{ label, value } in evaluationOptions" :key="value" :value="value">
            {{ label }}
          </option>
        </select>
      </div>
    </td>
  </StringTooltip>
  <td v-if="$slots.scc125">
    <slot name="scc125" />
  </td>
  <td>
    <div class="tw-grid tw-place-items-center">
      <CheckVerified
        :checked="evaluationVerified"
        :disabled="!canEditEvaluation || resolvedEvaluation === ''"
        @update:checked="(checked) => updateEvaluation(checked ? resolvedAIEvaluation || null : null)"
      />
    </div>
  </td>
</template>
